/**
 * Конвертує вік (роки + місяці) в Unix timestamp
 * Формула: поточна дата - повних років - повних місяців
 * @param {number} years - Повні роки (0-8, де 8 означає 8+)
 * @param {number} months - Повні місяці (0-11)
 * @returns {number} Unix timestamp дати народження
 */
export const calculateBirthdayTimestamp = (years, months = 0) => {
  // Обмежуємо 8+ як 8 років
  const actualYears = Math.min(years, 8);
  const actualMonths = Math.max(0, Math.min(months, 11));
  
  const now = new Date();
  
  // Віднімаємо роки та місяці від поточної дати
  const birthdayDate = new Date(
    now.getFullYear() - actualYears,
    now.getMonth() - actualMonths,
    now.getDate()
  );
  
  // Повертаємо Unix timestamp в секундах
  return Math.floor(birthdayDate.getTime() / 1000);
};

/**
 * Конвертує Unix timestamp в вік (роки + місяці)
 * @param {number} timestamp - Unix timestamp дати народження
 * @returns {object} { years, months } - Вік в роках та місяцях
 */
export const calculateAgeFromTimestamp = (timestamp) => {
  if (!timestamp || timestamp <= 0) {
    return { years: 4, months: 5 }; // Дефолтне значення
  }
  
  const birthDate = new Date(timestamp * 1000);
  const now = new Date();
  
  let years = now.getFullYear() - birthDate.getFullYear();
  let months = now.getMonth() - birthDate.getMonth();
  
  // Коригуємо, якщо місяці негативні
  if (months < 0) {
    years--;
    months += 12;
  }
  
  // Коригуємо, якщо день народження ще не пройшов в поточному місяці
  if (now.getDate() < birthDate.getDate()) {
    months--;
    if (months < 0) {
      years--;
      months += 12;
    }
  }
  
  // Обмежуємо максимальний вік до 8 років
  if (years > 8) {
    years = 8;
    months = 0;
  }
  
  // Обмежуємо мінімальний вік до 0
  if (years < 0) {
    years = 0;
    months = 0;
  }
  
  return { years, months };
};

/**
 * Перевіряє, чи дитина готова до навчання (4+ років)
 * @param {number} years - Повні роки
 * @param {number} months - Повні місяці
 * @returns {boolean} true, якщо готова до навчання
 */
export const isReadyForLearning = (years, months = 0) => {
  return years >= 4;
};

/**
 * Форматує вік для відображення
 * @param {number} years - Повні роки
 * @param {number} months - Повні місяці
 * @returns {string} Форматований текст віку
 */
export const formatAge = (years, months = 0) => {
  const yearsText = years === 8 ? "8+" : years.toString();
  
  if (years >= 6) {
    return `${yearsText} років`;
  }
  
  const monthsText = months === 1 ? "місяць" : 
                   months >= 2 && months <= 4 ? "місяці" : "місяців";
  
  if (years === 0) {
    return `${months} ${monthsText}`;
  }
  
  const yearText = years === 1 ? "рік" : 
                  years >= 2 && years <= 4 ? "роки" : "років";
  
  if (months === 0) {
    return `${yearsText} ${yearText}`;
  }
  
  return `${yearsText} ${yearText} ${months} ${monthsText}`;
};